import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createReceptionShare,
  fetchReceptionShareBatch,
  fetchReceptionShareBatches,
  fetchReceptionShareCandidates,
  fetchReceptionShareableGroups,
  revokeReceptionShare,
} from '@/services/api/receptionShares'
import type { ReceptionShareCreatePayload } from '@/types/receptionShare'

export function useReceptionShareCandidates(kind: 'source' | 'target') {
  return useQuery({
    queryKey: ['reception-shares', 'candidates', kind],
    queryFn: () => fetchReceptionShareCandidates({ kind, page_size: 100 }),
    staleTime: 30_000,
  })
}

export function useReceptionShareableGroups(sourceUserId?: number) {
  return useQuery({
    queryKey: ['reception-shares', 'groups', sourceUserId],
    queryFn: () => fetchReceptionShareableGroups({ source_user_id: sourceUserId!, page_size: 500 }),
    enabled: Boolean(sourceUserId),
  })
}

export function useReceptionShareBatches(params: { status?: string; keyword?: string; page: number; page_size: number }) {
  return useQuery({
    queryKey: ['reception-shares', 'batches', params],
    queryFn: () => fetchReceptionShareBatches(params),
  })
}

export function useReceptionShareBatch(batchId?: number) {
  return useQuery({
    queryKey: ['reception-shares', 'batch', batchId],
    queryFn: () => fetchReceptionShareBatch(batchId!),
    enabled: Boolean(batchId),
  })
}

export function useCreateReceptionShare() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: ReceptionShareCreatePayload) => createReceptionShare(payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['reception-shares'] }),
  })
}

export function useRevokeReceptionShare() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ batchId, reason }: { batchId: number; reason: string }) => revokeReceptionShare(batchId, reason),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['reception-shares'] }),
  })
}
